import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'
import { exportServiceBusinessUsage } from '@/modules/Service/services'
import React, { useState } from 'react'
import toast from 'react-hot-toast'

// Mirrors the servicemgmt export contract (metrics columns + views = sheets).
const METRICS = [
    { id: 'requests', label: 'Requests' },
    { id: 'volume', label: 'Volume' },
    { id: 'bandwidth', label: 'Bandwidth' },
]
const VIEWS = [
    {
        id: 'domain_day',
        label: 'Domain × day',
        desc: 'One row per domain per day.',
    },
    { id: 'day', label: 'By day', desc: 'Daily totals across all domains.' },
    { id: 'domain', label: 'By domain', desc: 'Period totals per domain.' },
    { id: 'geo', label: 'By geo', desc: 'Daily VN / China / Global split.' },
]

interface ExportDialogProps {
    serviceID: number
    gte: string
    lte: string
    serviceName?: string
    disabled?: boolean
}

const toggle = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id]

const filenameFrom = (disposition: string | undefined, fallback: string) => {
    const match = disposition?.match(/filename="?([^"]+)"?/i)
    return match?.[1] || fallback
}

const ExportDialog: React.FC<ExportDialogProps> = ({
    serviceID,
    gte,
    lte,
    serviceName = 'ContentDeliveryNetwork',
    disabled,
}) => {
    const [open, setOpen] = useState(false)
    const [metrics, setMetrics] = useState<string[]>([
        'requests',
        'volume',
        'bandwidth',
    ])
    const [views, setViews] = useState<string[]>([
        'domain_day',
        'day',
        'domain',
    ])
    const [loading, setLoading] = useState(false)

    const submit = async () => {
        if (!metrics.length || !views.length) {
            toast.error('Select at least one metric and one sheet')
            return
        }
        setLoading(true)
        try {
            const res = await exportServiceBusinessUsage({
                serviceID,
                gte,
                lte,
                metrics,
                views,
            })
            const blob = new Blob([res.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = filenameFrom(
                res.headers?.['content-disposition'],
                `${serviceName}_BusinessUsages.xlsx`
            )
            document.body.appendChild(link)
            link.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(link)
            setOpen(false)
        } catch {
            toast.error('Export failed. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={disabled}>
                    Export XLSX
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Export Business Usage</DialogTitle>
                    <DialogDescription>
                        Choose metrics and sheets. Uses the current date range.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div>
                        <p className="text-sm font-medium mb-2">Metrics</p>
                        <div className="flex flex-wrap gap-4">
                            {METRICS.map((m) => (
                                <label
                                    key={m.id}
                                    className="flex items-center gap-2 cursor-pointer"
                                >
                                    <Checkbox
                                        checked={metrics.includes(m.id)}
                                        onCheckedChange={() =>
                                            setMetrics((s) => toggle(s, m.id))
                                        }
                                    />
                                    <span className="text-sm">{m.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div>
                        <p className="text-sm font-medium mb-2">Sheets</p>
                        <div className="space-y-2">
                            {VIEWS.map((v) => (
                                <label
                                    key={v.id}
                                    className="flex items-start gap-2 cursor-pointer"
                                >
                                    <Checkbox
                                        checked={views.includes(v.id)}
                                        onCheckedChange={() =>
                                            setViews((s) => toggle(s, v.id))
                                        }
                                    />
                                    <span className="text-sm">
                                        <span className="font-medium">
                                            {v.label}
                                        </span>
                                        <span className="text-gray-500">
                                            {' '}
                                            — {v.desc}
                                        </span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setOpen(false)}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button size="sm" onClick={submit} disabled={loading}>
                        {loading ? 'Exporting…' : 'Export'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

export default ExportDialog
